
import { ReactNode } from "react";
import { Container } from "@/components/ui/container";
import { cn } from "@/lib/utils";

interface Feature {
  title: string;
  description: string;
  icon: ReactNode;
}

interface FeatureSectionProps {
  title: string;
  subtitle?: string;
  description?: string;
  features: Feature[];
  className?: string;
  columns?: 2 | 3 | 4;
  background?: "white" | "light" | "gradient";
  id?: string; // Added id prop
}

export const FeatureSection = ({
  title,
  subtitle,
  description,
  features,
  className,
  columns = 3,
  background = "white",
  id
}: FeatureSectionProps) => {
  const getBgClasses = () => {
    switch (background) {
      case "light":
        return "bg-gray-50";
      case "gradient":
        return "bg-gradient-to-b from-white to-gray-50";
      default:
        return "bg-white";
    }
  };

  return (
    <section id={id} className={cn("py-16 md:py-24", getBgClasses(), className)}>
      <Container>
        <div className="text-center mb-12">
          {subtitle && (
            <div className="inline-block px-4 py-2 bg-purple-100 rounded-full text-purple-700 font-medium text-sm mb-4">
              {subtitle}
            </div>
          )}
          <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl mb-6">
            {title}
          </h2>
          {description && (
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {description}
            </p>
          )}
        </div>

        <div className={cn(
          "grid gap-8",
          columns === 2 ? "sm:grid-cols-1 md:grid-cols-2" :
          columns === 3 ? "sm:grid-cols-2 md:grid-cols-3" :
          "sm:grid-cols-2 md:grid-cols-4"
        )}>
          {features.map((feature, index) => (
            <div 
              key={index} 
              className={cn(
                "bg-white p-6 md:p-8 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
              )}
            >
              <div className="rounded-full bg-purple-100 w-12 h-12 flex items-center justify-center mb-4">
                {feature.icon}
              </div>
              <h3 className="text-xl font-semibold mb-3 text-gray-900">{feature.title}</h3>
              <p className="text-gray-600">{feature.description}</p>
            </div>
          ))}
        </div>
      </Container>
    </section>
  );
};
